<template>
  <div style="
        background-color: #fff;
        border-top: 0px;
        border-radius: 0 0 3px 3px;
      ">
    <div id="tabFocusAreas" class="margin-subpages">
      <!-- Team Selector and Filters -->
      <div class="header-controls">
        <!-- Row 1: Team, Search, and Date Range -->
        <div class="header-row header-row-primary">
          <div class="header-left">
            <el-select
              v-model="selectedTeam"
              :placeholder="$t('performance.focus_areas.select_team')"
              filterable
              class="team-select"
              :class="{ 'blur-dropdown': blurMode }"
              @change="handleTeamChange"
            >
              <el-option
                v-if="user.user_type !== 'leadership'"
                :label="`${$t('performance.focus_areas.all_teams')} (${allTeamsCount})`"
                :value="'All Teams'"
                :class="{ 'blur-option': blurMode }"
              />
              <el-option
                v-for="team in teamOptions"
                :key="team.key"
                :label="team.name"
                :value="team.key"
                :class="{ 'blur-option': blurMode }"
              />
            </el-select>

            <el-input
              v-model="searchKeyword"
              :placeholder="$t('performance.focus_areas.search')"
              clearable
              class="search-input"
              @input="handleSearch"
            />
          </div>

          <div class="header-right">
            <el-select
              v-model="dateRange"
              class="date-range-select"
              @change="handleDateRangeChange"
            >
              <el-option
                v-for="option in dateRangeOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </div>
        </div>

        <!-- Row 2: Filters -->
        <div class="header-row header-row-filters">
          <div class="drop-down-container" :class="{ 'active': filterNoFocus }">
            <div class="text-drop-down" @click="toggleNoFocusFilter">
              {{ $t('performance.focus_areas.without_focus') }}
            </div>
            <div v-if="filterNoFocus" class="relative" style="width: 26px">
              <div @click="toggleNoFocusFilter" class="drop-down-close">
                <div class="x-btn"></div>
              </div>
            </div>
          </div>

          <div class="drop-down-container" :class="{ 'active': filterPartnered }">
            <div class="text-drop-down" @click="togglePartneredFilter">
              {{ $t('performance.focus_areas.with_partners') }}
            </div>
            <div v-if="filterPartnered" class="relative" style="width: 26px">
              <div @click="togglePartneredFilter" class="drop-down-close">
                <div class="x-btn"></div>
              </div>
            </div>
          </div>

          <el-select
            :value="filterGoalAlignment"
            :placeholder="$t('performance.focus_areas.goal_alignment')"
            class="goal-alignment-select"
            @change="setGoalAlignmentFilter"
          >
            <el-option :label="$t('performance.focus_areas.all_items')" value="all" />
            <el-option :label="$t('performance.focus_areas.with_goal')" value="aligned" />
            <el-option :label="$t('performance.focus_areas.without_goal')" value="unaligned" />
          </el-select>
        </div>
      </div>

      <!-- Summary Cards -->
      <div v-if="!loading" class="flex-center">
        <div class="box-card-border highlights mt-12 mb-24">
          <div class="summary-metrics">
            <!-- People Stats -->
            <div class="flex-column metric-block">
              <div class="fs-12 fw-700 mb-12">{{ $t('performance.focus_areas.people') }}</div>
              <div class="metric-row">
                <div class="metric-item">
                  <span class="fs-28 fw-600">{{ formatNumber(summaryStats.totalPeople) }}</span>
                  <span class="fs-13 fc-909399 ml-4">{{ $t('performance.focus_areas.total') }}</span>
                </div>
                <span class="metric-separator">·</span>
                <div class="metric-item">
                  <span class="fs-28 fw-600" style="color: #3AC47D;">{{ formatNumber(historicalSummary ? historicalSummary.usersWithFocus : summaryStats.withFocus) }}</span>
                  <span class="fs-13 fc-909399 ml-4">{{ $t('performance.focus_areas.with_focus') }}</span>
                  <el-tooltip 
                    v-if="dateRange === 7 && focusCoverageWeekChange && focusCoverageWeekChange.trend !== 'no_data'"
                    effect="light" 
                    placement="top"
                    popper-class="weekly-trend-tooltip"
                  >
                    <div slot="content" class="trend-tooltip-content">
                      <div class="trend-tooltip-header">{{ $t('performance.focus_areas.focus_coverage_trend') }}</div>
                      <div class="trend-sparkline">
                        <svg :viewBox="`0 0 ${focusCoverageTrendData.length > 1 ? (focusCoverageTrendData.length - 1) * 30 : 30} 40`" preserveAspectRatio="none">
                          <polyline
                            :points="getSparklinePoints(focusCoverageTrendData)"
                            fill="none"
                            :stroke="focusCoverageWeekChange.trend === 'up' ? '#3AC47D' : focusCoverageWeekChange.trend === 'down' ? '#F44336' : '#909399'"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <circle
                            v-if="focusCoverageTrendData.length > 0"
                            :cx="(focusCoverageTrendData.length - 1) * 30"
                            :cy="getSparklineY(focusCoverageTrendData[focusCoverageTrendData.length - 1].value, focusCoverageTrendData)"
                            r="3"
                            :fill="focusCoverageWeekChange.trend === 'up' ? '#3AC47D' : focusCoverageWeekChange.trend === 'down' ? '#F44336' : '#909399'"
                          />
                        </svg>
                      </div>
                      <div class="trend-tooltip-range">
                        <span>{{ focusCoverageTrendData.length > 0 ? Math.round(focusCoverageTrendData[0].value) : 0 }}%</span>
                        <span class="trend-arrow">→</span>
                        <span class="trend-current">{{ focusCoverageTrendData.length > 0 ? Math.round(focusCoverageTrendData[focusCoverageTrendData.length - 1].value) : 0 }}%</span>
                      </div>
                      <div class="trend-tooltip-period">{{ $t('performance.focus_areas.last') }} {{ focusCoverageTrendData.length }} {{ $t('performance.focus_areas.weeks') }}</div>
                    </div>
                    <span 
                      class="label-change cursor-pointer"
                      :class="{
                        'text-green': focusCoverageWeekChange.trend === 'up',
                        'text-red': focusCoverageWeekChange.trend === 'down',
                        'fc-6A6A6A': focusCoverageWeekChange.trend === 'neutral'
                      }"
                    >{{ focusCoverageWeekChange.change > 0 ? '+' : '' }}{{ focusCoverageWeekChange.change }}%</span>
                  </el-tooltip>
                </div>
                <span class="metric-separator">·</span>
                <div class="metric-item">
                  <span class="fs-28 fw-600" style="color: #909399;">{{ formatNumber(summaryStats.withoutFocus) }}</span>
                  <span class="fs-13 fc-909399 ml-4">{{ $t('performance.focus_areas.no_focus') }}</span>
                </div>
              </div>
            </div>

            <!-- Focus Items Stats -->
            <div class="flex-column metric-block items-block">
              <div class="metric-header-row">
                <span class="fs-12 fw-700">{{ $t('performance.focus_areas.focus_items') }}</span>
                <span v-if="historicalSummary" class="date-range-badge">
                  {{ $t('performance.focus_areas.last') }} {{ dateRange }} {{ $t('performance.focus_areas.days') }}
                </span>
                <span v-if="loadingHistorical" class="loading-indicator">
                  <i class="el-icon-loading"></i>
                </span>
              </div>
              <div class="items-content">
                <div class="items-value">
                  <span class="fs-32 fw-600" style="color: #6C30CC;">{{ formatNumber(historicalSummary ? historicalSummary.totalUniqueItems : summaryStats.totalItems) }}</span>
                  <span class="fs-13 fc-909399 ml-4">{{ historicalSummary ? $t('performance.focus_areas.unique') : $t('performance.focus_areas.total') }}</span>
                </div>
                <div class="items-breakdown">
                  <div class="item-stat open">
                    <span class="dot"></span>
                    <span class="num">{{ formatNumber(historicalSummary ? historicalSummary.openItems : (summaryStats.openItems || 0)) }}</span>
                    <span class="label">{{ $t('performance.focus_areas.open') }}</span>
                  </div>
                  <div class="item-stat done">
                    <span class="dot"></span>
                    <span class="num">{{ formatNumber(historicalSummary ? historicalSummary.completedItems : (summaryStats.doneItems || 0)) }}</span>
                    <span class="label">{{ $t('performance.focus_areas.done') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Visualizations Section -->
      <div v-if="!loading && paginatedUsers.length > 0">
        <div class="fs-15 fw-700 mt-24">{{ $t('performance.focus_areas.focus_insights') }}</div>

        <div class="split-chart" style="margin-top: 12px;">
          <el-tabs v-model="activeChartTab" class="chart-tabs">
            <el-tab-pane :label="$t('performance.focus_areas.focus_distribution')" name="distribution">
              <div class="chart-container" style="height: 400px; padding: 24px 16px 12px;">
                <div class="flex-center ml-52 pl-20 mb-16">
                  <div class="recognition-title open-bar ml-10 mr-6 mb-2"></div>
                  <div class="fs-12 fw-700 opa-06 mr-12">{{ $t('performance.focus_areas.in_progress') }}</div>
                  <div class="recognition-title done-bar ml-10 mr-6 mb-2"></div>
                  <div class="fs-12 fw-700 opa-06">{{ $t('performance.focus_areas.completed') }}</div>
                </div>
                <canvas ref="distributionChart"></canvas>
              </div>
            </el-tab-pane>
            <el-tab-pane :label="$t('performance.focus_areas.team_metrics')" name="metrics">
              <div class="team-metrics-table" style="padding: 16px;">
                <el-table
                  ref="teamMetricsTable"
                  :data="hierarchicalTeamMetrics"
                  style="width: 100%"
                  row-key="teamId"
                  lazy
                  :load="loadTeamChildren"
                  :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
                  :default-sort="{ prop: sortColumn, order: sortDirection === 'asc' ? 'ascending' : 'descending' }"
                  :default-expand-all="false"
                >
                  <el-table-column
                    prop="teamName"
                    min-width="220"
                    sortable
                    header-align="left"
                  >
                    <template slot="header">
                      <span>{{ $t('performance.focus_areas.team') }}</span>
                    </template>
                    <template slot-scope="scope">
                      <span class="team-name-cell" :class="{ 'blur-text': blurMode }">{{ scope.row.teamName }}</span>
                    </template>
                  </el-table-column>

                  <el-table-column
                    prop="totalPeople"
                    width="100"
                    align="center"
                    header-align="center"
                    sortable
                  >
                    <template slot="header">
                      <el-tooltip :content="$t('performance.focus_areas.tooltip_total_people')" placement="top" effect="dark">
                        <span style="cursor: help; border-bottom: 1px dotted #909399;">{{ $t('performance.focus_areas.people') }}</span>
                      </el-tooltip>
                    </template>
                    <template slot-scope="scope">
                      <span class="metric-text">{{ scope.row.totalPeople }}</span>
                    </template>
                  </el-table-column>

                  <el-table-column
                    prop="percentWithFocus"
                    width="140"
                    sortable
                    header-align="left"
                  >
                    <template slot="header">
                      <el-tooltip :content="$t('performance.focus_areas.tooltip_focusing')" placement="top" effect="dark">
                        <span style="cursor: help; border-bottom: 1px dotted #909399;">{{ $t('performance.focus_areas.focusing') }}</span>
                      </el-tooltip>
                    </template>
                    <template slot-scope="scope">
                      <div class="metric-cell">
                        <div class="metric-bar" :style="{ width: scope.row.percentWithFocus + '%', backgroundColor: getEngagementColor(scope.row.percentWithFocus) }"></div>
                        <span class="metric-value">{{ scope.row.percentWithFocus.toFixed(0) }}%</span>
                      </div>
                    </template>
                  </el-table-column>

                  <el-table-column
                    width="160"
                    align="center"
                    header-align="center"
                  >
                    <template slot="header">
                      <el-tooltip :content="$t('performance.focus_areas.tooltip_items_open_done')" placement="top" effect="dark">
                        <span style="cursor: help; border-bottom: 1px dotted #909399;">{{ $t('performance.focus_areas.items_open_done') }}</span>
                      </el-tooltip>
                    </template>
                    <template slot-scope="scope">
                      <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
                        <span style="color: #6C30CC; font-weight: 600; font-size: 15px;">{{ scope.row.openItems }}</span>
                        <span style="color: #909399; font-weight: 400;">/</span>
                        <span style="color: #3AC47D; font-weight: 600; font-size: 15px;">{{ scope.row.doneItems }}</span>
                      </div>
                    </template>
                  </el-table-column>

                  <el-table-column
                    prop="completionRate"
                    width="140"
                    sortable
                    header-align="left"
                  >
                    <template slot="header">
                      <el-tooltip :content="$t('performance.focus_areas.tooltip_completion')" placement="top" effect="dark">
                        <span style="cursor: help; border-bottom: 1px dotted #909399;">{{ $t('performance.focus_areas.completion') }}</span>
                      </el-tooltip>
                    </template>
                    <template slot-scope="scope">
                      <div class="metric-cell">
                        <div class="metric-bar" :style="{ width: scope.row.completionRate + '%', backgroundColor: getCompletionColor(scope.row.completionRate) }"></div>
                        <span class="metric-value">{{ scope.row.completionRate.toFixed(0) }}%</span>
                      </div>
                    </template>
                  </el-table-column>

                  <el-table-column
                    prop="partnershipRate"
                    width="140"
                    sortable
                    header-align="left"
                  >
                    <template slot="header">
                      <el-tooltip :content="$t('performance.focus_areas.tooltip_partnership')" placement="top" effect="dark">
                        <span style="cursor: help; border-bottom: 1px dotted #909399;">{{ $t('performance.focus_areas.partnership') }}</span>
                      </el-tooltip>
                    </template>
                    <template slot-scope="scope">
                      <div class="metric-cell">
                        <div class="metric-bar" :style="{ width: scope.row.partnershipRate + '%', backgroundColor: '#6366F1' }"></div>
                        <span class="metric-value">{{ scope.row.partnershipRate.toFixed(0) }}%</span>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </el-tab-pane>
        </el-tabs>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading">
        <!-- Summary Cards Skeleton -->
        <div class="flex-center">
          <div class="box-card-border highlights mt-12 mb-24">
            <div class="grid-4-20">
              <div v-for="i in 4" :key="`metric-${i}`" class="flex-column mr-24">
                <div class="skeleton-label mb-12"></div>
                <div class="skeleton-value"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart Section Skeleton -->
        <div class="fs-15 fw-700 mt-24 mb-12">
          <div class="skeleton-text" style="width: 150px; height: 20px;"></div>
        </div>
        <div class="split-chart" style="margin-top: 12px;">
          <div style="padding: 24px;">
            <div class="skeleton-chart"></div>
          </div>
        </div>

        <!-- People List Skeleton -->
        <div style="margin-top: 32px;">
          <div v-for="i in 3" :key="`person-${i}`" class="person-row-skeleton">
            <div class="skeleton-avatar"></div>
            <div class="skeleton-person-info">
              <div class="skeleton-text" style="width: 140px; height: 16px; margin-bottom: 8px;"></div>
              <div class="skeleton-text" style="width: 100px; height: 14px;"></div>
            </div>
            <div class="skeleton-focus-area">
              <div class="skeleton-box"></div>
              <div class="skeleton-box"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="paginatedUsers.length === 0" class="center" style="padding: 80px 0;">
        <div style="text-align: center;">
          <div style="font-size: 48px; color: #dcdfe6; margin-bottom: 16px;">
            <i class="el-icon-document"></i>
          </div>
          <div style="font-size: 18px; font-weight: 600; color: #777777; margin-bottom: 8px;">
            {{ searchKeyword || filterNoFocus || filterPartnered ? $t('performance.focus_areas.no_results_found') : $t('performance.focus_areas.no_focus_data_available') }}
          </div>
          <div style="font-size: 14px; color: #909399;">
            {{ searchKeyword || filterNoFocus || filterPartnered ? $t('performance.focus_areas.adjust_filters_hint') : $t('performance.focus_areas.empty_state_hint') }}
          </div>
        </div>
      </div>

      <!-- People List -->
      <div v-else class="people-list" style="margin-top: 16px;">
        <div
          v-for="person in paginatedUsers"
          :key="person.email"
          class="person-row"
          :class="{ 
            'no-focus-row': person.activeItems.length === 0 
          }"
        >
            <div class="person-info" :class="{ 'blur-text': blurMode }">
              <div class="avatar-container">
                <img
                  v-if="person.large_pic && !person.imageError"
                  :src="person.large_pic"
                  class="avatar"
                  :style="{ borderColor: person.house_color || '#ddd' }"
                  @error="handleImageError(person)"
                />
                <div
                  v-else
                  class="avatar avatar-placeholder"
                  :style="{ borderColor: person.house_color || '#ddd', backgroundColor: person.house_color || '#ddd' }"
                >
                  {{ getInitials(person.firstname, person.lastname) }}
                </div>
              </div>
              <div class="person-details">
                <div class="person-name">{{ person.fullname }}</div>
                <div class="person-team">{{ person.team_name }}</div>
              </div>
            </div>

            <div class="focus-items">
              <div v-if="person.activeItems.length === 0" class="no-focus-badge">
                {{ $t('performance.focus_areas.no_focus_items') }}
              </div>
              <div v-else class="focus-sections-grid">
                <!-- In Progress Column -->
                <div class="focus-column open-column">
                  <div class="column-header">
                    <i class="el-icon-loading"></i>
                    {{ $t('performance.focus_areas.in_progress') }} ({{ getOpenItems(person).length }})
                  </div>
                  <div v-if="getOpenItems(person).length > 0" class="column-items">
                    <div v-for="item in getOpenItems(person)" :key="item.item_id" class="focus-item-row">
                      <div class="item-content" @click="openPartnerDrawer(person, item)">
                        <span class="item-text">{{ item.item }}</span>
                        <el-tooltip
                          v-if="item.goal_id && getGoalName(item.goal_id)"
                          :content="getGoalName(item.goal_id)"
                          placement="top"
                        >
                          <span class="goal-link-badge"><i class="el-icon-aim"></i></span>
                        </el-tooltip>
                      </div>
                      <div v-if="getPartners(item.item_id).length > 0" class="partner-avatars">
                        <el-tooltip
                          v-for="(partner, index) in getPartners(item.item_id).slice(0, 3)"
                          :key="partner.email"
                          :content="`${partner.fullname || partner.email} ${partner.team_name ? '(' + partner.team_name + ')' : ''}`"
                          placement="top"
                        >
                          <img
                            v-if="partner.large_pic && !partner.imageError"
                            :src="partner.large_pic"
                            class="partner-avatar"
                            :style="{ zIndex: 10 - index, borderColor: partner.house_color || '#fff' }"
                            @error="handlePartnerImageError(partner)"
                          />
                          <div
                            v-else
                            class="partner-avatar partner-avatar-placeholder"
                            :style="{ zIndex: 10 - index, backgroundColor: partner.house_color || '#ddd' }"
                          >
                            {{ getInitials(partner.firstname || '', partner.lastname || '') }}
                          </div>
                        </el-tooltip>
                        <div v-if="getPartners(item.item_id).length > 3" class="partner-more">
                          +{{ getPartners(item.item_id).length - 3 }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="column-empty">
                    {{ $t('performance.focus_areas.no_items_in_progress') }}
                  </div>
                </div>

                <!-- Completed Column -->
                <div class="focus-column done-column">
                  <div class="column-header">
                    <i class="el-icon-check"></i>
                    {{ $t('performance.focus_areas.completed') }} ({{ getDoneItems(person).length }})
                  </div>
                  <div v-if="getDoneItems(person).length > 0" class="column-items">
                    <div v-for="item in getDoneItems(person)" :key="item.item_id" class="focus-item-row">
                      <div class="item-content done-item" @click="openPartnerDrawer(person, item)">
                        <span class="item-text">{{ item.item }}</span>
                        <el-tooltip
                          v-if="item.goal_id && getGoalName(item.goal_id)"
                          :content="getGoalName(item.goal_id)"
                          placement="top"
                        >
                          <span class="goal-link-badge"><i class="el-icon-aim"></i></span>
                        </el-tooltip>
                      </div>
                      <div v-if="getPartners(item.item_id).length > 0" class="partner-avatars">
                        <el-tooltip
                          v-for="(partner, index) in getPartners(item.item_id).slice(0, 3)"
                          :key="partner.email"
                          :content="`${partner.fullname || partner.email} ${partner.team_name ? '(' + partner.team_name + ')' : ''}`"
                          placement="top"
                        >
                          <img
                            v-if="partner.large_pic && !partner.imageError"
                            :src="partner.large_pic"
                            class="partner-avatar"
                            :style="{ zIndex: 10 - index, borderColor: partner.house_color || '#fff' }"
                            @error="handlePartnerImageError(partner)"
                          />
                          <div
                            v-else
                            class="partner-avatar partner-avatar-placeholder"
                            :style="{ zIndex: 10 - index, backgroundColor: partner.house_color || '#ddd' }"
                          >
                            {{ getInitials(partner.firstname || '', partner.lastname || '') }}
                          </div>
                        </el-tooltip>
                        <div v-if="getPartners(item.item_id).length > 3" class="partner-more">
                          +{{ getPartners(item.item_id).length - 3 }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="column-empty">
                    {{ $t('performance.focus_areas.no_completed_items') }}
                  </div>
                </div>
              </div>
            </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination-container" style="margin-top: 24px; text-align: center;">
          <el-pagination
            :current-page="currentPage"
            :page-size="50"
            :total="filteredUsers.length"
            layout="prev, pager, next"
            @current-change="handlePageChange"
          />
        </div>
      </div>
    </div>

    <!-- Partner Drawer -->
    <el-drawer
      :visible.sync="drawerVisible"
      :with-header="false"
      size="500px"
      direction="rtl"
      custom-class="focus-drawer"
    >
      <div v-if="selectedItem" class="drawer-content">
        <div class="drawer-header">
          <div class="drawer-title">{{ $t('performance.focus_areas.focus_item_details') }}</div>
          <div class="drawer-user-info" :class="{ 'blur-text': blurMode }">
            <div class="avatar-container-drawer">
              <img
                v-if="selectedPerson.large_pic && !selectedPerson.imageError"
                :src="selectedPerson.large_pic"
                class="avatar-drawer"
                :style="{ borderColor: selectedPerson.house_color || '#ddd' }"
                @error="handleImageError(selectedPerson)"
              />
              <div
                v-else
                class="avatar-drawer avatar-placeholder"
                :style="{ backgroundColor: selectedPerson.house_color || '#ddd' }"
              >
                {{ getInitials(selectedPerson.firstname, selectedPerson.lastname) }}
              </div>
            </div>
            <div class="drawer-user-details">
              <div class="drawer-user-name">{{ selectedPerson.fullname }}</div>
              <div class="drawer-user-team">{{ selectedPerson.team_name }}</div>
            </div>
          </div>
        </div>

        <div class="focus-detail-section">
          <div class="section-label">{{ $t('performance.focus_areas.focus_item_label') }}</div>
          <div class="section-content focus-item-title">
            {{ selectedItem.item }}
          </div>
        </div>

        <div v-if="selectedItem.description" class="focus-detail-section">
          <div class="section-label">{{ $t('performance.focus_areas.description_label') }}</div>
          <div class="section-content">
            {{ selectedItem.description }}
          </div>
        </div>

        <div class="focus-detail-section">
          <div class="section-label">{{ $t('performance.focus_areas.status_label') }}</div>
          <el-tag :type="getStatusType(selectedItem.status)" size="medium">
            {{ selectedItem.status || 'open' }}
          </el-tag>
        </div>

        <el-divider style="margin: 24px 0;" />

        <div class="focus-detail-section">
          <div class="partners-section-header">
            <span class="section-label" style="font-size: 15px; font-weight: 700; text-transform: none; letter-spacing: 0;">
              {{ $t('performance.focus_areas.focus_partners') }}
            </span>
            <el-badge
              v-if="currentPartners.length > 0"
              :value="currentPartners.length"
              class="partner-badge"
            />
          </div>

          <div v-if="loadingPartners" class="center" style="padding: 40px 0;">
            <i class="el-icon-loading" style="font-size: 24px; color: #6C30CC;"></i>
          </div>

          <div v-else-if="currentPartners.length === 0" class="center" style="padding: 40px 0;">
            <div style="text-align: center; color: #909399;">
              <div style="font-size: 32px; margin-bottom: 8px;">
                <i class="el-icon-user"></i>
              </div>
              <div style="font-size: 14px;">{{ $t('performance.focus_areas.no_partners_for_item') }}</div>
            </div>
          </div>

          <div v-else class="partners-list">
              <div
                v-for="partner in currentPartners"
                :key="`${partner.partner_email}-${partner.partner_item_id}`"
                class="partner-card"
              >
                <div class="partner-header">
                  <div class="partner-info" :class="{ 'blur-text': blurMode }">
                    <div class="avatar-container-small">
                      <img
                        v-if="getPartnerUser(partner.partner_email).large_pic && !getPartnerUser(partner.partner_email).imageError"
                        :src="getPartnerUser(partner.partner_email).large_pic"
                        class="avatar-small"
                        :style="{ borderColor: getPartnerUser(partner.partner_email).house_color || '#ddd' }"
                        @error="handlePartnerImageError(getPartnerUser(partner.partner_email))"
                      />
                      <div
                        v-else
                        class="avatar-small avatar-placeholder"
                        :style="{ backgroundColor: getPartnerUser(partner.partner_email).house_color || '#ddd' }"
                      >
                        {{ getInitials(getPartnerUser(partner.partner_email).firstname, getPartnerUser(partner.partner_email).lastname) }}
                      </div>
                    </div>
                    <div class="partner-name-section">
                      <div class="partner-name">
                        {{ getPartnerUser(partner.partner_email).fullname }}
                      </div>
                      <div class="partner-team">{{ getPartnerTeamName(partner.partner_email) }}</div>
                    </div>
                  </div>
                  <el-tag :type="getStatusType(partner.partner_status)" size="small">
                    {{ partner.partner_status || 'open' }}
                  </el-tag>
                </div>

              <div v-if="partner.partner_view_description" class="partner-description">
                <div class="description-label">{{ $t('performance.focus_areas.partners_view') }}</div>
                <div class="description-text">{{ partner.partner_view_description }}</div>
              </div>

              <div v-if="partner.user_view_description" class="partner-description">
                <div class="description-label">{{ $t('performance.focus_areas.your_view') }}</div>
                <div class="description-text">{{ partner.user_view_description }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import { getDateRangeOptions } from "@/store/focus";

export default {
  name: "focus-areas",
  data() {
    return {
      drawerVisible: false,
      selectedPerson: null,
      selectedItem: null,
      currentPartners: [],
      activeChartTab: 'metrics',
      distributionChartInstance: null,
      sortColumn: 'percentWithFocus',
      sortDirection: 'desc',
    };
  },
  computed: {
    user() {
      return this.$store.getters["authen/user"];
    },
    dateRangeOptions() {
      // Resolve labels through the current i18n dictionary so the
      // select reflects the active language.
      return getDateRangeOptions();
    },
    loading() {
      return this.$store.getters["focus/loading"];
    },
    loadingPartners() {
      return this.$store.getters["focus/loadingPartners"];
    },
    selectedTeam: {
      get() {
        return this.$store.getters["focus/selectedTeam"];
      },
      set(value) {
        this.$store.commit("focus/setSelectedTeam", value);
      },
    },
    searchKeyword: {
      get() {
        return this.$store.getters["focus/searchKeyword"];
      },
      set(value) {
        this.$store.commit("focus/setSearchKeyword", value);
      },
    },
    filterNoFocus() {
      return this.$store.getters["focus/filterNoFocus"];
    },
    filterPartnered() {
      return this.$store.getters["focus/filterPartnered"];
    },
    filterGoalAlignment() {
      return this.$store.getters["focus/filterGoalAlignment"];
    },
    goalsTeamAI() {
      return this.$store.getters["goals/goalsTeamAI"];
    },
    paginatedUsers() {
      return this.$store.getters["focus/paginatedUsers"];
    },
    filteredUsers() {
      return this.$store.getters["focus/filteredUsers"];
    },
    summaryStats() {
      return this.$store.getters["focus/summaryStats"];
    },
    currentPage() {
      return this.$store.getters["focus/currentPage"];
    },
    totalPages() {
      return this.$store.getters["focus/totalPages"];
    },
    teamOptions() {
      return this.$store.getters["team/teamsall"];
    },
    allTeamsCount() {
      return this.$store.getters["team/teamsall"].length;
    },
    partnerCache() {
      return this.$store.getters["focus/partnerCache"];
    },
    allUsers() {
      return this.$store.getters["focus/allUsers"];
    },
    allTeams() {
      return this.$store.getters["focus/allTeams"];
    },
    blurMode() {
      return this.$store.getters["demomode/blurMode"];
    },
    focusDistribution() {
      return this.$store.getters["focus/focusDistribution"];
    },
    focusDistributionWithStatus() {
      return this.$store.getters["focus/focusDistributionWithStatus"];
    },
    teamMetrics() {
      const metrics = this.$store.getters["focus/teamMetrics"];
      return this.sortTeamMetrics(metrics);
    },
    hierarchicalTeamMetrics() {
      const metrics = this.$store.getters["focus/teamMetrics"];
      return this.buildTeamHierarchy(metrics);
    },
    partnersMap() {
      return this.$store.getters["focus/partnersMap"];
    },
    // Historical focus data computed properties
    dateRange: {
      get() {
        return this.$store.getters["focus/dateRange"];
      },
      set(value) {
        this.$store.commit("focus/setDateRange", value);
      },
    },
    loadingHistorical() {
      return this.$store.getters["focus/loadingHistorical"] || 
             this.$store.getters["focus/isLoadingHistoricalTeam"](this.selectedTeam, true);
    },
    historicalSummary() {
      return this.$store.getters["focus/getHistoricalSummaryStats"](this.selectedTeam, true);
    },
    historicalFocusCache() {
      return this.$store.getters["focus/getHistoricalFocusForTeam"](this.selectedTeam, true);
    },
    // Weekly change computed properties
    focusCoverageWeekChange() {
      return this.$store.getters["focus/getFocusCoverageWeekChange"](this.selectedTeam);
    },
    loadingWeeklySummary() {
      return this.$store.getters["focus/isLoadingWeeklySummary"](this.selectedTeam);
    },
    // Multi-week trend data for tooltip (last 4 weeks)
    focusCoverageTrendData() {
      return this.$store.getters["focus/getWeeklyTrendData"](this.selectedTeam, 'focusCoverage', 4);
    },
  },
  watch: {
    activeChartTab(newTab) {
      if (newTab === 'distribution') {
        this.$nextTick(() => {
          this.renderDistributionChart();
        });
      } else if (newTab === 'metrics') {
        this.$nextTick(() => {
          this.expandRootTeams();
        });
      }
    },
    focusDistribution() {
      if (this.activeChartTab === 'distribution') {
        this.$nextTick(() => {
          this.renderDistributionChart();
        });
      }
    },
    paginatedUsers(newUsers) {
      // When data first appears and the distribution tab is active,
      // ensure the chart is rendered (handles v-if mounting timing).
      if (newUsers && newUsers.length > 0 && this.activeChartTab === 'distribution') {
        this.$nextTick(() => {
          this.renderDistributionChart();
        });
      }
    },
    hierarchicalTeamMetrics(newMetrics) {
      // Auto-expand root teams when data loads
      if (newMetrics && newMetrics.length > 0) {
        this.$nextTick(() => {
          this.expandRootTeams();
        });
      }
    },
  },
  async created() {
    try {
      await this.$store.dispatch("team/getTeamsOptions", this.user);

      // Set default team selection
      if (this.user.user_type === 'leadership') {
        this.selectedTeam = this.user.team || '';
      } else {
        this.selectedTeam = 'All Teams';
      }



        this.$store.dispatch("focus/loadFocusData", this.selectedTeam),
        this.$store.dispatch("goals/getGoalsTeamAI"),
        this.loadHistoricalData()

      // Load weekly summary for week-over-week changes
      this.loadWeeklySummary();

      // Load historical focus data
      //await this.loadHistoricalData();
    } catch (error) {
      console.error('Error initializing focus areas:', error);
      this.$message.error('Failed to load focus areas data');
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.activeChartTab === 'distribution') {
        this.renderDistributionChart();
      } else if (this.activeChartTab === 'metrics') {
        // Give the table time to render before expanding
        setTimeout(() => {
          this.expandRootTeams();
        }, 100);
      }
    });
  },
  beforeDestroy() {
    if (this.distributionChartInstance) {
      this.distributionChartInstance.destroy();
    }
  },
  methods: {
    // Sparkline helper methods for trend tooltips
    getSparklinePoints(data) {
      if (!data || data.length === 0) return '';
      const values = data.map(d => d.value);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const range = max - min || 1;
      
      return data.map((item, index) => {
        const x = index * 30;
        const y = 40 - ((item.value - min) / range) * 36 - 2;
        return `${x},${y}`;
      }).join(' ');
    },
    getSparklineY(value, data) {
      if (!data || data.length === 0) return 20;
      const values = data.map(d => d.value);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const range = max - min || 1;
      return 40 - ((value - min) / range) * 36 - 2;
    },
    async handleTeamChange(team) {
      await this.$store.dispatch("focus/changeTeam", team);
      // Reload historical data for new team
      await this.loadHistoricalData();
      // Reload weekly summary for new team
      await this.loadWeeklySummary();
    },
    async handleDateRangeChange(value) {
      await this.$store.dispatch("focus/setDateRange", { dateRange: value, reload: false });
      // Reload historical data with new date range
      await this.loadHistoricalData();
    },
    async loadHistoricalData() {
      try {
        console.time('loadHistoricalData')
        await this.$store.dispatch("focus/loadHistoricalFocusDataForTeam", {
          teamId: this.selectedTeam,
          includeChildren: true,
        });
        console.timeEnd('loadHistoricalData')
      } catch (error) {
        console.error('Error loading historical focus data:', error);
      }
    },
    async loadWeeklySummary() {
      try {
        await this.$store.dispatch("focus/loadWeeklySummaryForTeam", {
          teamId: this.selectedTeam || 'All Teams',
        });
      } catch (error) {
        console.error('Error loading weekly summary:', error);
      }
    },
    handleSearch(keyword) {
      this.$store.dispatch("focus/searchUsers", keyword);
    },
    toggleNoFocusFilter() {
      this.$store.dispatch("focus/toggleFilterNoFocus");
    },
    togglePartneredFilter() {
      this.$store.dispatch("focus/toggleFilterPartnered");
    },
    setGoalAlignmentFilter(value) {
      this.$store.dispatch("focus/setGoalAlignmentFilter", value);
    },
    getGoalName(goalId) {
      if (!goalId || !this.goalsTeamAI) return null;
      // Search through all teams' goals to find the matching goal
      for (const teamId of Object.keys(this.goalsTeamAI)) {
        const goals = this.goalsTeamAI[teamId];
        const goal = goals.find(g => g.goal_id === goalId);
        if (goal) {
          return goal.quick_preview;
        }
      }
      return null;
    },
    handlePageChange(page) {
      this.$store.dispatch("focus/setPage", page);
    },
    getInitials(firstname, lastname) {
      const first = firstname ? firstname.charAt(0).toUpperCase() : '';
      const last = lastname ? lastname.charAt(0).toUpperCase() : '';
      return first + last || '?';
    },
    getPartnerCount(itemId) {
      const partners = this.partnerCache[itemId];
      return partners ? partners.length : 0;
    },
    getPartners(itemId) {
      return this.partnersMap[itemId] || [];
    },
    getOpenItems(person) {
      return person.activeItems.filter(item => item.status !== 'done');
    },
    getDoneItems(person) {
      return person.activeItems.filter(item => item.status === 'done');
    },
    filterByTeam(teamId) {
      this.selectedTeam = teamId;
      this.handleTeamChange(teamId);
    },
    getEngagementColor(percentage) {
      if (percentage >= 75) return '#3AC47D';
      if (percentage >= 50) return '#E6A23C';
      if (percentage >= 25) return '#F56C6C';
      return '#909399';
    },
    getCompletionColor(percentage) {
      if (percentage >= 75) return '#3AC47D';
      if (percentage >= 50) return '#E6A23C';
      if (percentage >= 25) return '#F56C6C';
      return '#909399';
    },
    sortTeamMetrics(metrics) {
      const sorted = [...metrics];
      sorted.sort((a, b) => {
        let aVal = a[this.sortColumn];
        let bVal = b[this.sortColumn];

        // Handle string sorting for team names
        if (this.sortColumn === 'teamName') {
          aVal = (aVal || '').toLowerCase();
          bVal = (bVal || '').toLowerCase();
          return this.sortDirection === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        // Numeric sorting
        if (this.sortDirection === 'asc') {
          return aVal - bVal;
        } else {
          return bVal - aVal;
        }
      });
      return sorted;
    },
    renderDistributionChart() {
      if (!this.$refs.distributionChart) return;
      
      if (this.distributionChartInstance) {
        this.distributionChartInstance.destroy();
      }

      const ctx = this.$refs.distributionChart.getContext('2d');
      const data = this.focusDistributionWithStatus.slice(0, 10); // Top 10

      this.distributionChartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: data.map(d => d.name),
          datasets: [
            {
              label: 'In Progress',
              data: data.map(d => d.openCount),
              backgroundColor: '#6C30CC',
              borderColor: '#6C30CC',
              borderWidth: 1,
            },
            {
              label: 'Completed',
              data: data.map(d => d.doneCount),
              backgroundColor: '#3AC47D',
              borderColor: '#3AC47D',
              borderWidth: 1,
            }
          ]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
            },
            title: {
              display: true,
              text: 'Top 10 Focus Areas (Open vs Completed)',
              font: {
                size: 16,
                weight: 'bold',
              },
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  return `${context.dataset.label}: ${context.parsed.x} items`;
                },
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              beginAtZero: true,
              ticks: {
                stepSize: 1,
              },
            },
            y: {
              stacked: true,
            },
          },
        },
      });
    },
    async openPartnerDrawer(person, item) {
      this.selectedPerson = person;
      this.selectedItem = item;
      this.drawerVisible = true;
      
      const partners = await this.$store.dispatch("focus/loadPartnersForItem", item.item_id);
      this.currentPartners = partners || [];
    },
    getPartnerUser(email) {
      const user = this.allUsers[email];
      if (!user) {
        // Extract name from email for better display
        const emailUsername = email.split('@')[0];
        const formattedName = emailUsername
          .split(/[._-]/)
          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ');

        return {
          fullname: formattedName,
          firstname: formattedName.split(' ')[0] || '',
          lastname: formattedName.split(' ')[1] || '',
          large_pic: null,
          house_color: '#ddd',
          email: email,
        };
      }

      // User found - normalize the data structure
      return {
        ...user,
        fullname: user.fullname || `${user.firstname || ''} ${user.lastname || ''}`.trim() || email,
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        large_pic: user.large_pic || user.profilepic || user.profilepic_crop || user.profilepic_small || null,
        house_color: user.house_color || '#ddd',
        email: email,
      };
    },
    getPartnerTeamName(email) {
      const user = this.allUsers[email];
      if (!user) return '';

      // Try to get team name from the user's team property
      if (user.team) {
        const team = this.allTeams[user.team];
        if (team && team.name) {
          return team.name;
        }
      }

      // Fallback to team_name if it exists directly on user object
      if (user.team_name) {
        return user.team_name;
      }

      return '';
    },
    getStatusType(status) {
      if (!status || status === 'open') return 'info';
      if (status === 'done') return 'success';
      return 'warning';
    },
    handleImageError(person) {
      this.$set(person, 'imageError', true);
    },
    handlePartnerImageError(partner) {
      this.$set(partner, 'imageError', true);
    },
    buildTeamHierarchy(metrics) {
      if (!metrics || metrics.length === 0) return [];

      // Get all teams from the store
      const allTeams = this.$store.getters["focus/allTeams"];
      const selectedTeam = this.$store.getters["focus/selectedTeam"];

      // Create a map of team metrics by team ID for quick lookup
      const metricsMap = {};
      metrics.forEach(metric => {
        metricsMap[metric.teamId] = metric;
      });

      // Find root teams
      // If a specific team is selected, treat that team as the root
      // Otherwise, show actual root teams (teams with no parent or parent = 'root')
      const rootTeams = metrics.filter(metric => {
        const team = allTeams[metric.teamId];
        if (!team) return false;

        // If a team is selected, only show that team as root
        if (selectedTeam && selectedTeam !== '' && !selectedTeam.includes('All Teams')) {
          return metric.teamId === selectedTeam;
        }

        // Otherwise show actual root teams
        const parentId = team.parent;
        return !parentId || parentId === 'root' || !allTeams[parentId];
      });

      // Build hierarchy with first-level children pre-loaded
      return rootTeams.map(rootTeam => {
        const teamNode = {
          ...rootTeam,
          hasChildren: this.hasChildTeams(rootTeam.teamId, allTeams, metricsMap),
        };

        // Pre-load first-level children for root teams
        const firstLevelChildren = [];
        Object.keys(allTeams).forEach(childTeamId => {
          const team = allTeams[childTeamId];
          if (team.parent === rootTeam.teamId && metricsMap[childTeamId]) {
            firstLevelChildren.push({
              ...metricsMap[childTeamId],
              hasChildren: this.hasChildTeams(childTeamId, allTeams, metricsMap),
            });
          }
        });

        if (firstLevelChildren.length > 0) {
          teamNode.children = firstLevelChildren;
        }

        return teamNode;
      });
    },
    hasChildTeams(teamId, allTeams, metricsMap) {
      return Object.keys(allTeams).some(childTeamId => {
        const team = allTeams[childTeamId];
        // Check if this team has the current teamId as parent AND has metrics
        return team.parent === teamId && metricsMap[childTeamId];
      });
    },
    loadTeamChildren(tree, _treeNode, resolve) {
      const teamId = tree.teamId;
      const allTeams = this.$store.getters["focus/allTeams"];
      const allMetrics = this.$store.getters["focus/teamMetrics"];

      // Create metrics map
      const metricsMap = {};
      allMetrics.forEach(metric => {
        metricsMap[metric.teamId] = metric;
      });

      // Find children for this team
      const children = [];
      Object.keys(allTeams).forEach(childTeamId => {
        const team = allTeams[childTeamId];
        if (team.parent === teamId && metricsMap[childTeamId]) {
          children.push({
            ...metricsMap[childTeamId],
            hasChildren: this.hasChildTeams(childTeamId, allTeams, metricsMap),
          });
        }
      });

      resolve(children);
    },
    handleTeamRowClick(row) {
      // Filter to show only this team's data
      if (row.teamId) {
        this.filterByTeam(row.teamId);
      }
    },
    expandRootTeams() {
      if (this.$refs.teamMetricsTable && this.hierarchicalTeamMetrics.length > 0) {
        this.hierarchicalTeamMetrics.forEach(rootTeam => {
          this.$refs.teamMetricsTable.toggleRowExpansion(rootTeam, true);
        });
      }
    },
    /**
     * Format number with commas for thousands
     */
    formatNumber(num) {
      if (num === null || num === undefined) return '0';
      return num.toLocaleString();
    },
  },
};
</script>

<style scoped>
.team-metrics-table ::v-deep .el-table__body-wrapper.is-scrolling-none {
  min-height: auto !important;
}
.margin-subpages {
  margin: 0 24px;
}

/* Header Controls Layout */
.header-controls {
  padding: 24px 0 16px 0;
}

.header-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-row-primary {
  justify-content: space-between;
  margin-bottom: 12px;
}

.header-row-filters {
  gap: 8px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-left .team-select {
  width: 200px;
}

.header-left .search-input {
  width: 200px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-right .date-range-select {
  width: 150px;
}

.goal-alignment-select {
  width: 140px;
}

.center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.relative {
  position: relative;
}

.highlights {
  width: 896px;
  overflow: hidden;
}

.box-card-border {
  box-shadow:
    0px 0.3px 0.9px rgba(0, 0, 0, 0.008),
    0px 0.8px 2.1px rgba(0, 0, 0, 0.012),
    0px 1.5px 4px rgba(0, 0, 0, 0.015),
    0px 2.7px 7.1px rgba(0, 0, 0, 0.018),
    0px 5px 13.4px rgba(0, 0, 0, 0.022),
    0px 12px 32px rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  padding: 24px;
  width: 100%;
}

/* Summary Metrics */
.summary-metrics {
  display: flex;
  align-items: flex-start;
  gap: 40px;
}

.metric-block {
  min-width: 180px;
}

.metric-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.metric-header-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.date-range-badge {
  font-size: 11px;
  font-weight: 500;
  color: #6C30CC;
  background: rgba(108, 48, 204, 0.1);
  padding: 2px 8px;
  border-radius: 10px;
}

.loading-indicator {
  color: #6C30CC;
  font-size: 14px;
}

.date-range-select {
  width: 100%;
}

.metric-item {
  display: flex;
  align-items: baseline;
}

.metric-separator {
  font-size: 24px;
  color: #dcdfe6;
  font-weight: 300;
}

.fs-28 {
  font-size: 28px;
}

.fs-32 {
  font-size: 32px;
}

.fc-909399 {
  color: #909399;
}

.ml-4 {
  margin-left: 4px;
}

/* Focus Items Block */
.items-block {
  flex: 1;
  border-left: 1px solid #eee;
  padding-left: 40px;
}

.items-content {
  display: flex;
  align-items: center;
  gap: 32px;
}

.items-value {
  display: flex;
  align-items: baseline;
}

.items-breakdown {
  display: flex;
  gap: 20px;
}

.item-stat {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
}

.item-stat .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.item-stat.open .dot {
  background: #6C30CC;
}

.item-stat.done .dot {
  background: #3AC47D;
}

.item-stat .num {
  font-weight: 700;
  font-size: 16px;
}

.item-stat.open .num {
  color: #6C30CC;
}

.item-stat.done .num {
  color: #3AC47D;
}

.item-stat .label {
  color: #909399;
}

/* Week-over-week change indicator styles (inline with main score) */
.label-change {
  font-size: 11px;
  font-weight: 700;
  margin-left: 6px;
}

.text-green {
  color: #3AC47D;
}

.text-red {
  color: #F44336;
}

.fc-6A6A6A {
  color: #6A6A6A;
}

.cursor-pointer {
  cursor: pointer;
}

.grid-4-20 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.flex-column {
  display: flex;
  flex-direction: column;
}

.flex-row {
  display: flex;
  flex-direction: row;
}

.people-list {
  margin-bottom: 24px;
}

.person-row {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  padding: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  margin-bottom: 16px;
  transition: all 0.3s;
  gap: 20px;
  background: #ffffff;
  box-shadow:
    0px 0.3px 0.9px rgba(0, 0, 0, 0.008),
    0px 0.8px 2.1px rgba(0, 0, 0, 0.012),
    0px 1.5px 4px rgba(0, 0, 0, 0.015);
}

.person-row.no-focus-row {
  padding: 16px 20px;
  min-height: auto;
  align-items: center;
  background: linear-gradient(90deg, #fffbeb 0%, #ffffff 50%);
  border-color: #fcd34d;
}

.person-row:hover {
  border-color: #d0d0d0;
  transform: translateY(-2px);
  box-shadow:
    0px 2px 4px rgba(0, 0, 0, 0.05),
    0px 4px 12px rgba(0, 0, 0, 0.08);
}

@media (prefers-reduced-motion: reduce) {
  .person-row:hover {
    transform: none;
  }
}

.person-info {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 260px;
  flex-shrink: 0;
}

.avatar-container {
  flex-shrink: 0;
}

.avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 3px solid;
  object-fit: cover;
}

.avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 18px;
}

.person-details {
  flex: 1;
  min-width: 0;
}

.person-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
  line-height: 1.3;
}

.person-team {
  font-size: 13px;
  color: #909399;
  line-height: 1.4;
}

.focus-items {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.no-focus-badge {
  font-size: 14px;
  color: #b45309;
  font-weight: 600;
  padding: 10px 18px;
  background: #fef3c7;
  border-radius: 8px;
  border: 1px solid #f59e0b;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.no-focus-badge::before {
  content: "⚠";
  font-size: 14px;
}

.focus-sections-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  flex: 1;
}

@media (max-width: 768px) {
  .focus-sections-grid {
    grid-template-columns: 1fr;
  }
}

.focus-column {
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 90px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.open-column {
  background: #F5F9FF;
  border: 1px solid #E3EFFF;
}

.done-column {
  background: #F5FBF7;
  border: 1px solid #E3F5EB;
}

.column-header {
  padding: 12px 14px;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #606266;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.5);
}

.open-column .column-header {
  color: #6C30CC;
  background: rgba(108, 48, 204, 0.08);
}

.done-column .column-header {
  color: #3AC47D;
  background: rgba(58, 196, 125, 0.08);
}

.column-items {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.column-empty {
  padding: 20px 14px;
  text-align: center;
  font-size: 12px;
  color: #909399;
  font-style: italic;
}

.focus-item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  background: white;
  border-radius: 6px;
  transition: all 0.2s;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.04);
  border: 1px solid transparent;
}

.focus-item-row:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transform: translateX(3px);
  border-color: rgba(108, 48, 204, 0.15);
}

@media (prefers-reduced-motion: reduce) {
  .focus-item-row:hover {
    transform: none;
  }
}

.item-content {
  flex: 1;
  cursor: pointer;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.item-text {
  font-size: 14px;
  color: #303133;
  word-break: break-word;
  line-height: 1.5;
}

.done-item .item-text {
  color: #3AC47D;
  font-weight: 600;
}

.goal-link-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-left: 8px;
  background: #6C30CC;
  border-radius: 50%;
  color: white;
  font-size: 11px;
  flex-shrink: 0;
  cursor: help;
}

.goal-link-badge i {
  font-size: 11px;
}

.partner-avatars {
  display: flex;
  align-items: center;
  margin-left: -4px;
}

.partner-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid white;
  object-fit: cover;
  margin-left: -8px;
  cursor: pointer;
  transition: all 0.2s;
}

.partner-avatar:hover {
  transform: scale(1.3);
  z-index: 20 !important;
}

.partner-avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 10px;
}

.partner-more {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #6C30CC;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  margin-left: -8px;
  border: 2px solid white;
}

/* Drawer Styles */
.drawer-content {
  padding: 32px 24px;
  height: 100%;
  overflow-y: auto;
  background: #ffffff;
}

.drawer-header {
  margin-bottom: 32px;
  border-bottom: 1px solid #e8e8e8;
  padding-bottom: 24px;
}

.drawer-title {
  font-size: 20px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 20px;
}

.drawer-user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-container-drawer {
  flex-shrink: 0;
}

.avatar-drawer {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid;
}

.drawer-user-details {
  flex: 1;
}

.drawer-user-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.drawer-user-team {
  font-size: 14px;
  color: #909399;
}

.focus-detail-section {
  margin-bottom: 24px;
}

.section-label {
  font-size: 11px;
  color: #909399;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
}

.section-content {
  font-size: 14px;
  color: #303133;
  line-height: 1.6;
}

.focus-item-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.partners-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.partner-badge ::v-deep .el-badge__content {
  background-color: #6C30CC;
  border: none;
  font-weight: 600;
}

.partners-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.partner-card {
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s;
}

.partner-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-color: #d0d0d0;
}

.partner-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.partner-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.avatar-container-small {
  flex-shrink: 0;
}

.avatar-small {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid;
}

.partner-name-section {
  flex: 1;
  min-width: 0;
}

.partner-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.partner-team {
  font-size: 13px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.partner-description {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.description-label {
  font-size: 12px;
  color: #606266;
  font-weight: 600;
  margin-bottom: 4px;
}

.description-text {
  font-size: 13px;
  color: #303133;
  line-height: 1.5;
}

.blur-text {
  filter: blur(4px);
  pointer-events: none;
}

/* Blur dropdown text only - keeps interactive */
.blur-dropdown ::v-deep .el-input__inner {
  filter: blur(4px);
}

/* Drawer Global Styles */
.focus-drawer {
  background: #fafafa;
}

.focus-drawer ::v-deep .el-drawer__body {
  padding: 0;
  background: #fafafa;
}

.split-chart {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.08);
}

.chart-tabs {
  background: white;
}

.chart-tabs ::v-deep .el-tabs__header {
  margin-bottom: 0;
  padding: 0 16px;
  background: #fafafa;
}

.chart-tabs ::v-deep .el-tabs__nav-wrap::after {
  height: 1px;
  background-color: #e0e0e0;
}

.chart-tabs ::v-deep .el-tabs__item {
  font-weight: 600;
  font-size: 14px;
  color: #606266;
  padding: 0 20px;
  height: 48px;
  line-height: 48px;
}

.chart-tabs ::v-deep .el-tabs__item.is-active {
  color: #6C30CC;
}

.chart-tabs ::v-deep .el-tabs__active-bar {
  background-color: #6C30CC;
  height: 3px;
}

.chart-container {
  background: white;
}

.recognition-title {
  display: inline-block;
}

.recognition-title.open-bar {
  width: 16px;
  height: 16px;
  background-color: #6C30CC;
  border-radius: 50%;
}

.recognition-title.done-bar {
  width: 16px;
  height: 16px;
  background-color: #3AC47D;
  border-radius: 50%;
}

.flex-center {
  display: flex;
  align-items: center;
}

.metrics-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.metrics-table th {
  background: #fafafa;
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #303133;
  border-bottom: 1px solid #e8e8e8;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;
}

.sortable-header:hover {
  background: #f0f0f0;
}

.metrics-table td {
  padding: 14px 12px;
  border-bottom: 1px solid #e8e8e8;
  font-size: 14px;
  color: #303133;
}

.metrics-table tbody tr {
  transition: all 0.2s;
  background: white;
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  background: #fafafa;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.metric-cell {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}

.metric-bar {
  height: 20px;
  border-radius: 4px;
  transition: width 0.3s;
  min-width: 2px;
}

.metric-value {
  font-weight: 600;
  min-width: 40px;
  font-size: 13px;
}

.metric-text {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.text-skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
}

.image-skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.fs-12 {
  font-size: 12px;
}

.fs-14 {
  font-size: 14px;
}

.fs-15 {
  font-size: 15px;
}

.fs-16 {
  font-size: 16px;
}

.fs-24 {
  font-size: 24px;
}

.fw-500 {
  font-weight: 500;
}

.fw-600 {
  font-weight: 600;
}

.fw-700 {
  font-weight: 700;
}

.fc-909399 {
  color: #909399;
}

.drop-down-container {
  margin-left: 0;
  height: 40px;
  border-radius: 4px;
  padding: 0 8px 0 12px;
  display: flex;
  align-items: center;
  transition: background 0.2s;
}

.drop-down-container.active {
  background-color: rgba(50, 194, 213, 0.1);
}

.text-drop-down {
  color: #00ACC1;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
}

.drop-down-close {
  position: absolute;
  top: -12px;
  transform: rotate(45deg);
  width: 24px;
  height: 24px;
  margin-left: 4px;
}

.x-btn {
  position: relative;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.x-btn:before,
.x-btn:after {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  content: "";
  background-color: #747474;
  transition: 0.3s;
}

.x-btn:before {
  width: 2px;
  height: 20px;
}

.x-btn:after {
  width: 20px;
  height: 2px;
}

.x-btn:hover:before,
.x-btn:hover:after {
  background-color: #333;
}

.team-select,
.search-input {
  width: 100%;
}

.team-name-cell {
  font-weight: 500;
  color: #303133;
  cursor: pointer;
  transition: color 0.2s;
}

.team-name-cell:hover {
  color: #6C30CC;
}

.team-metrics-table ::v-deep .el-table {
  font-size: 14px;
}

.team-metrics-table ::v-deep .el-table th {
  background: #fafafa;
  border-bottom: 2px solid #e0e0e0;
}

.team-metrics-table ::v-deep .el-table th .cell {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #2e2e2e;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.team-metrics-table ::v-deep .el-table th .caret-wrapper {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  height: 14px;
  width: 14px;
  margin-left: 4px;
}

.team-metrics-table ::v-deep .el-table td,
.team-metrics-table ::v-deep .el-table th {
  padding: 14px 0;
}

.team-metrics-table ::v-deep .el-table td {
  border-bottom: 1px solid #f0f0f0;
}

.team-metrics-table ::v-deep .el-table__row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.team-metrics-table ::v-deep .el-table__row:hover {
  background-color: #fafafa;
}

.team-metrics-table ::v-deep .el-table__expand-icon {
  color: #909399;
  font-size: 14px;
  transition: color 0.2s;
}

.team-metrics-table ::v-deep .el-table__expand-icon:hover {
  color: #6C30CC;
}

/* Skeleton Loading Styles */
.skeleton-label {
  width: 80px;
  height: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
}

.skeleton-value {
  width: 100px;
  height: 28px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
}

.skeleton-text {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
}

.skeleton-chart {
  width: 100%;
  height: 280px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 8px;
}

.person-row-skeleton {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 9px;
  margin-bottom: 12px;
  background: #fff;
}

.skeleton-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  flex-shrink: 0;
}

.skeleton-person-info {
  display: flex;
  flex-direction: column;
  min-width: 200px;
}

.skeleton-focus-area {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  flex: 1;
}

.skeleton-box {
  height: 90px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 6px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>

<style>
/* Weekly trend tooltip styles with sparkline */
.weekly-trend-tooltip {
  padding: 0 !important;
}

.trend-tooltip-content {
  padding: 12px 16px;
  min-width: 160px;
}

.trend-tooltip-header {
  font-size: 12px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 10px;
}

.trend-sparkline {
  width: 100%;
  height: 40px;
  margin-bottom: 10px;
}

.trend-sparkline svg {
  width: 100%;
  height: 100%;
}

.trend-tooltip-range {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 4px;
}

.trend-tooltip-range .trend-arrow {
  color: #c0c4cc;
  font-size: 11px;
}

.trend-tooltip-range .trend-current {
  color: #303133;
  font-weight: 700;
}

.trend-tooltip-period {
  text-align: center;
  font-size: 11px;
  color: #909399;
}

/* Blur dropdown options - global style needed for el-select dropdown */
.blur-option span {
  filter: blur(4px);
}
</style>

